import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type { BulkEditEquipmentResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * Bulk edit (Story 12.6, R-E10, spec judgement call 5) — set one shared
 * field across every selected equipment row in a single atomic write
 * (`POST /equipment/bulk-edit`, VC-12.6-08). The field list here is the
 * frontend's own half of the allowlist — server-side re-validates the
 * identical set independently (`equipment_repo.BULK_EDIT_ALLOWED_FIELDS`,
 * VC-12.6-10) and never trusts this restriction alone.
 *
 * `value` always travels to the API as a plain string (`_convert_bulk_edit_
 * value`, `app/api/equipment.py`) — this dialog only has to render the
 * right *input control* per field (a date picker, a Yes/No select, plain
 * text), not worry about wire encoding beyond that.
 */

type BulkEditableField =
  | "purchase_date"
  | "retailer_or_installer"
  | "warranty_type"
  | "warranty_length"
  | "warranty_expiration_date"
  | "has_extended_warranty"
  | "claim_phone_number"
  | "service_interval"
  | "service_interval_months";

//: Mirrors `equipment_repo.BULK_EDIT_ALLOWED_FIELDS`'s own exact nine
//: fields (this story's own Intent: the whole Warranty group, plus Purchase
//: date and the Service-interval pair — identity and per-item-estimate
//: fields deliberately excluded).
const BULK_EDIT_FIELDS: BulkEditableField[] = [
  "purchase_date",
  "retailer_or_installer",
  "warranty_type",
  "warranty_length",
  "warranty_expiration_date",
  "has_extended_warranty",
  "claim_phone_number",
  "service_interval",
  "service_interval_months",
];

const DATE_FIELDS = new Set<BulkEditableField>(["purchase_date", "warranty_expiration_date"]);
const BOOL_FIELDS = new Set<BulkEditableField>(["has_extended_warranty"]);
const INT_FIELDS = new Set<BulkEditableField>(["service_interval_months"]);

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export function BulkEditEquipmentDialog({
  open,
  onOpenChange,
  selectedIds,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onApplied: () => void;
}) {
  const [field, setField] = useState<BulkEditableField>(BULK_EDIT_FIELDS[0]!);
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<BulkEditEquipmentResource | null>(null);

  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  useEffect(() => {
    if (!open) {
      setField(BULK_EDIT_FIELDS[0]!);
      setValue("");
      setError(undefined);
      setResult(null);
    }
  }, [open]);

  function handleFieldChange(next: string) {
    setField(next as BulkEditableField);
    setValue("");
  }

  const canSubmit = selectedIds.length > 0 && value !== "" && !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const outcome = await apiFetch<BulkEditEquipmentResource>("/equipment/bulk-edit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedIds, field, value }),
      });
      setResult(outcome);
      onApplied();
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const selectedCount = selectedIds.length;

  return (
    <Container open={open} onOpenChange={onOpenChange} title="Bulk edit equipment">
      {result ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-body text-ink">
            {result.written_count} of {selectedCount} record{selectedCount === 1 ? "" : "s"} updated.
            {result.skipped_count > 0
              ? ` ${result.skipped_count} already replaced or removed and ${result.skipped_count === 1 ? "was" : "were"} skipped.`
              : ""}
          </p>
          <div className="flex justify-end">
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <p className="text-body text-ink-secondary">
            Set one field across {selectedCount} selected record{selectedCount === 1 ? "" : "s"} — one real,
            atomic write, useful when a building was appliance-refreshed all at once.
          </p>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Field</span>
            <Select autoFocus value={field} onChange={(event) => handleFieldChange(event.target.value)}>
              {BULK_EDIT_FIELDS.map((option) => (
                <option key={option} value={option}>
                  {labelFor(option)}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">New value</span>
            {DATE_FIELDS.has(field) ? (
              <Input type="date" value={value} onChange={(event) => setValue(event.target.value)} />
            ) : BOOL_FIELDS.has(field) ? (
              <Select value={value} onChange={(event) => setValue(event.target.value)}>
                <option value="">Choose…</option>
                {YES_NO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : INT_FIELDS.has(field) ? (
              <Input
                type="number"
                min="1"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder="e.g. 12"
              />
            ) : (
              <Input value={value} onChange={(event) => setValue(event.target.value)} />
            )}
          </label>

          {error ? (
            <p role="alert" className="text-meta text-danger">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? "Applying…" : `Apply to ${selectedCount}`}
            </Button>
          </div>
        </form>
      )}
    </Container>
  );
}
